/// <amd-module name="Build/Admin/Pool/Components/PoolJobsContainer" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";

import { TaskAgentJobRequest, TaskAgentPool, TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { ITeamProject } from "TFS/Services/TfsProjectService";

import { JobsFilterBar } from "Build/Admin/Common/Components/JobsFilterBar";
import { JobsList, AgentColumnStyle } from "Build/Admin/Common/Components/JobsList";
import { Assets } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { SignalRState } from "Build/Admin/Common/Stores/SignalRStore";
import { showPoolAgents, showPoolAgentSpecs } from "Build/Admin/Common/TaskAgentPoolUtils";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IPoolJobsContainerProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
    filterToggled: ObservableValue<boolean>;
    onFilterToggled(): void;
}

export interface IPoolJobsContainerState {
    agentColumnStyle: AgentColumnStyle;
    jobs: TaskAgentJobRequest[];
    projects: ITeamProject[];
    signalRState: SignalRState;
    renderZeroData: boolean;
    renderFilterBar: boolean;
}

export class PoolJobsContainer extends VssComponent<IPoolJobsContainerProps, IPoolJobsContainerState> {
    constructor(props: IPoolJobsContainerProps) {
        super(props);
        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.agentStore.addChangedListener(this._onChanged);
        this.props.storesHub.agentDefinitionStore.addChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
        this.props.storesHub.jobStore.addChangedListener(this._onChanged);
        this.props.storesHub.projectStore.addChangedListener(this._onChanged);
        this.props.storesHub.signalRStore.addChangedListener(this._onChanged);
        this.props.storesHub.jobStore.filter.subscribe(this._onChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.agentStore.removeChangedListener(this._onChanged);
        this.props.storesHub.agentDefinitionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.jobStore.removeChangedListener(this._onChanged);
        this.props.storesHub.projectStore.removeChangedListener(this._onChanged);
        this.props.storesHub.signalRStore.removeChangedListener(this._onChanged);
        this.props.storesHub.jobStore.filter.unsubscribe(this._onChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        // renderZeroData, showing filter: the filter is causing zero data
        let zeroDataPrimaryText: string = Resources.ZeroFilteredJobsPrimaryText;
        let zeroDataSecondaryText: string = Resources.ZeroFilteredJobsSecondaryText;

        // renderZeroData, no filter: there's no data at all
        if (this.state.renderZeroData && !this.state.renderFilterBar) {
            zeroDataPrimaryText = Resources.ZeroPoolJobsPrimaryText;
            zeroDataSecondaryText = Resources.ZeroPoolJobsSecondaryText;
        }

        return (
            <>
                <ConditionalChildren renderChildren={this.state.renderFilterBar && this.props.filterToggled}>
                    <div className="page-content-top page-content-left page-content-right">
                        {this._renderFilterBar()}
                    </div>
                </ConditionalChildren>
                <div className="pipelines-pool-jobs page-content page-content-top">
                    {
                        this.state.renderZeroData &&
                        <ZeroData
                            primaryText={zeroDataPrimaryText}
                            secondaryText={zeroDataSecondaryText}
                            imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                            imageAltText="" />
                    }
                    {
                        !this.state.renderZeroData &&
                        <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                            <JobsList
                                agentColumnStyle={this.state.agentColumnStyle}
                                agentLinkProvider={this._getLinkToAgent}
                                jobs={this.state.jobs}
                                projects={this.state.projects}
                                signalRState={this.state.signalRState}
                            />
                        </Card>
                    }
                </div>
            </>
        );
    }

    private _renderFilterBar = (): JSX.Element => {
        return <JobsFilterBar
            agentColumnStyle={this.state.agentColumnStyle}
            agents={this.props.storesHub.agentStore.state}
            agentDefinitions={this.props.storesHub.agentDefinitionStore.state}
            projects={this.state.projects}
            filter={this.props.storesHub.jobStore.filter}
            onFilterToggled={this.props.onFilterToggled} />;
    };

    private _getLinkToAgent = (job: TaskAgentJobRequest): string | undefined => {
        const selectedAgentQueue: TaskAgentQueue | undefined = this.props.storesHub.selectionStore.state.selectedAgentQueue;
        const selectedAgentPool: TaskAgentPool | undefined = this.props.storesHub.selectionStore.state.selectedAgentPool;

        if (job.reservedAgent && (selectedAgentQueue || selectedAgentPool)) {
            const linkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            return linkingService.getAgentUrl(selectedAgentQueue, selectedAgentPool, job.reservedAgent);
        }
        else {
            return undefined;
        }
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IPoolJobsContainerState {
        const selectedAgentPool: TaskAgentPool | undefined = this.props.storesHub.selectionStore.state.selectedAgentPool;
        const projects: ITeamProject[] = this.props.storesHub.projectStore.state;
        const filteredJobs: TaskAgentJobRequest[] = this.props.storesHub.jobStore.getFilteredJobs(projects, true);
        return {
            agentColumnStyle: this._getAgentColumnStyle(selectedAgentPool),
            jobs: filteredJobs,
            projects,
            signalRState: this.props.storesHub.signalRStore.state,
            renderZeroData: filteredJobs.length === 0,
            renderFilterBar: this.props.storesHub.jobStore.state.length !== 0
        };
    }

    private _getAgentColumnStyle(agentPool: TaskAgentPool | undefined): AgentColumnStyle {
        if (showPoolAgentSpecs(agentPool)) {
            return AgentColumnStyle.AgentSpec;
        }
        else if (showPoolAgents(agentPool)) {
            return AgentColumnStyle.Agent;
        }
        else {
            return AgentColumnStyle.None;
        }
    }
}
